import React, { createContext, useContext, useState, useCallback } from "react";
import { useBluelagoon } from "@bluelagoon";
import { useWorkflowGeneration } from "./WorkflowGenerationContext";
import { useWorkflow } from "./WorkflowContext";
import { useWorkflowExecution } from "./WorkflowExecutionContext";

const BrowserAutomationContext = createContext();

export const useBrowserAutomation = () => {
    const context = useContext(BrowserAutomationContext);
    if (!context) {
        throw new Error(
            "useBrowserAutomation must be used within a BrowserAutomationProvider"
        );
    }
    return context;
};

const useProvideBrowserAutomation = () => {
    const [browserSessions, setBrowserSessions] = useState({});
    const [aiSteps, setAiSteps] = useState([]);
    const { steps } = useWorkflow();
    const { handleAddStep, activeStep, getStepConfig, handleSetStepsConfig } =
        useWorkflowGeneration();
    const { testData, stepExecutionResult } = useWorkflowExecution();

    const { call: performBrowserAction } = useBluelagoon({
        method: "performBrowserAction",
    });

    const { call: insertNewBrowserSession } = useBluelagoon({
        method: "insertNewBrowserSession",
    });

    const activeBrowserSession = React.useMemo(() => {
        return browserSessions[activeStep];
    }, [browserSessions, activeStep]);

    const isRecording = React.useMemo(() => {
        return browserSessions[activeStep]?.isRecording;
    }, [browserSessions, activeStep]);

    const getBrowserSession = useCallback(
        (stepId) => {
            return browserSessions[stepId] || null;
        },
        [browserSessions]
    );

    const getCurrentStepData = useCallback(
        ({ activeStep, stepExecutionResult, steps }) => {
            const currentStepIndex = steps.findIndex(
                (step) => step.id === activeStep
            );

            const stepData =
                stepExecutionResult?.slice(
                    0,
                    currentStepIndex === -1
                        ? stepExecutionResult.length - 1
                        : currentStepIndex
                ) ?? [];

            return stepData;
        },
        []
    );

    const startBrowserSession = useCallback(async () => {
        if (!activeStep) {
            await handleAddStep("BROWSER_AUTOMATION", {
                aiSteps: [],
            });
        }
        if (browserSessions[activeStep]) {
            return;
        }
        const sessionResponse = await insertNewBrowserSession({
            // integrationId,
            returnSession: false,
        });

        console.log(sessionResponse);

        if (sessionResponse?.data) {
            setBrowserSessions((prev) => ({
                ...prev,
                [activeStep]: {
                    ...sessionResponse.data,
                    stepId: activeStep,
                    sessionUrl:
                        sessionResponse.data.sessionUrl ||
                        sessionResponse.data.url,
                    isRecording: true,
                    aiSteps: [], // Preserve existing aiSteps
                },
            }));
        }
    }, [activeStep, handleAddStep, activeStep]);

    const stopBrowserSession = useCallback(async () => {
        setBrowserSessions((prev) => {
            const newBrowserSessions = {
                ...prev,
                [activeStep]: { ...prev[activeStep], isRecording: false },
            };
            return newBrowserSessions;
        });
    }, [activeStep]);

    const executeInstruction = useCallback(
        async (instruction) => {
            if (!instruction || instruction.trim() === "") {
                return;
            }
            const id = Date.now();
            setAiSteps((prev) => {
                const newAiSteps = [
                    ...prev,
                    {
                        id,
                        instruction,
                        status: "executing",
                    },
                ];

                handleSetStepsConfig(activeStep, {
                    aiSteps: newAiSteps,
                });

                return newAiSteps;
            });

            const browserSession = getBrowserSession(activeStep);
            if (!browserSession) {
                return;
            }

            const stepData = getCurrentStepData({
                activeStep: activeStep,
                stepExecutionResult: stepExecutionResult,
                steps: steps,
            });

            const actionResponse = await performBrowserAction({
                sessionId: browserSession.id,
                observeInstruction: instruction,
                stepData,
                initialData: testData,
                history: aiSteps,
            });

            setAiSteps((prev) => {
                const newAiSteps = prev.map((step) => {
                    if (step.id === id) {
                        const newStep = {
                            ...step,
                            status: "completed",
                            action: actionResponse?.data?.actionType,
                            playwrightCode:
                                actionResponse?.data?.result?.playwrightDetails
                                    ?.code,
                        };

                        return newStep;
                    }
                    return step;
                });

                handleSetStepsConfig(activeStep, {
                    aiSteps: newAiSteps,
                });

                return newAiSteps;
            });

            // {
            //     "status": 200,
            //     "data": {
            //         "actionType": "navigate",
            //         "result": {
            //             "action": "navigate",
            //             "success": true,
            //             "dataUsed": {
            //                 "stepData": null,
            //                 "initialData": null,
            //                 "fields": []
            //             },
            //             "playwrightDetails": {
            //                 "code": "await page.goto('https://www.agentui.ai');",
            //                 "description": "Navigates to the website https://www.agentui.ai.",
            //                 "reasoning": "The instruction is to navigate to a specific URL, which is a straightforward navigation action. The available data does not provide any URL to navigate to, so the instruction itself contains the necessary information. There is no need to loop through any data as the action is a single navigation event.",
            //                 "dataAnalysis": {
            //                     "usesStepData": false,
            //                     "usesInitialData": false,
            //                     "specificFields": [],
            //                     "isLoop": false,
            //                     "loopTarget": null
            //                 }
            //             }
            //         }
            //     }
            // }

            console.log(actionResponse);
        },
        [
            activeStep,
            handleSetStepsConfig,
            getBrowserSession,
            performBrowserAction,
        ]
    );

    const removeAiStep = useCallback(
        (stepId) => {
            setAiSteps((prev) => {
                const newAiSteps = prev.filter((step) => step.id !== stepId);

                handleSetStepsConfig(activeStep, {
                    aiSteps: newAiSteps,
                });

                return newAiSteps;
            });
        },
        [activeStep, handleSetStepsConfig]
    );

    React.useEffect(() => {
        if (activeStep) {
            const stepConfig = getStepConfig(activeStep);

            if (stepConfig?.aiSteps?.length > 0) {
                setAiSteps(stepConfig.aiSteps);
            } else {
                setAiSteps([]);
            }
        } else {
            setAiSteps([]);
        }
    }, [activeStep, getStepConfig]);

    return {
        isRecording,
        startBrowserSession,
        aiSteps,
        stopBrowserSession,
        executeInstruction,
        removeAiStep,
        activeBrowserSession,
    };
};

export const BrowserAutomationProvider = ({ children }) => {
    const value = useProvideBrowserAutomation();
    return (
        <BrowserAutomationContext.Provider value={value}>
            {children}
        </BrowserAutomationContext.Provider>
    );
};
